/**
 * Centralized SEO Configuration for REDEBINS pages
 *
 * This file contains page-specific SEO data for all REDEBINS pages.
 * SEO data is sourced from the customer's spreadsheet and should be kept
 * in sync with their marketing/SEO requirements.
 *
 * City pages have their own SEO in service-area-city-content.tsx via SERVICE_AREA_CONTENT
 */

export interface PageSEO {
  title: string;
  description: string;
  keywords?: string[];
}

/**
 * Maps pathname to page-specific SEO data
 * City pages (e.g., /service-areas/dumpster-rental-minneapolis-mn)
 * are handled separately in service-area-city-content.tsx
 */
export const REDEBINS_SEO_CONFIG: Record<string, PageSEO> = {
  '/': {
    title:
      'Dumpster Rental | Twin Cities | Minneapolis & St. Paul | Seven County Metro Area',
    description:
      "You're just a few clicks from getting a RED-E-BIN dumpster rental. Our system is designed for smooth online booking to quick delivery and pick up.",
    keywords: [
      'dumpster rental',
      'dumpster rental Minneapolis',
      'dumpster rental St. Paul',
      'dumpster rental Twin Cities',
      'residential dumpster rental',
      'commercial dumpster rental',
      '5 yard dumpster',
      '10 yard dumpster',
      'roll off dumpster',
      'junk removal',
      'waste removal',
      'construction debris',
      'Seven County Metro Area',
    ],
  },
  '/contact': {
    title: 'Contact Us If You Need Help With Your RED-E-BIN Reservation',
    description:
      "We're here if you need help with your RED-E-BIN reservation, to answer any questions, or to make changes if needed. Contact us today.",
    keywords: [
      'contact RED-E-BINS',
      'dumpster rental contact',
      'Twin Cities dumpster help',
      'RED-E-BIN reservation',
      'customer service',
    ],
  },
  '/junk-removal': {
    title:
      'Junk Removal Services in Minneapolis & St. Paul | RED-E-BINS Minnesota',
    description:
      'RED-E-BINS is here for quick and easy junk removal across Minneapolis and St. Paul. From home clutter to construction debris, we make junk removal simple.',
    keywords: [
      'junk removal Minneapolis',
      'junk removal St. Paul',
      'junk removal Twin Cities',
      'home clutter removal',
      'construction debris removal',
      'RED-E-BINS junk removal',
    ],
  },
  '/residential': {
    title:
      'Residential Dumpster Rentals in Minneapolis & St. Paul | RED-E-BINS',
    description:
      'Residential Dumpster | We Are The Best In The Binz! Better customer service & less hassle than your regular trash service and too-small trash bin.',
    keywords: [
      'residential dumpster rental',
      'home dumpster rental Minneapolis',
      'residential dumpster St. Paul',
      'home cleanout dumpster',
      'garage cleanout dumpster',
      'renovation dumpster rental',
    ],
  },
  '/commercial': {
    title:
      'Commercial Construction & Demolition | RED-E-BINS Minnesota | Twin Cities',
    description:
      'Need a commercial construction dumpster in the Twin Cities? On-demand. On-time. On-budget. Extra-easy demolition dumpster rental with just one click.',
    keywords: [
      'commercial dumpster rental',
      'construction dumpster Twin Cities',
      'demolition dumpster rental',
      'commercial waste removal',
      'construction debris dumpster',
      'contractor dumpster rental',
    ],
  },
  '/prohibited-items': {
    title: "What Can You Throw in a Dumpster? | RED-E-BINS Minnesota",
    description:
      "Find out what you can and can't put in a RED-E-BIN dumpster. Most cleanout and remodeling debris is accepted. Check our guide or call us with questions.",
    keywords: [
      'what can you put in a dumpster',
      'prohibited dumpster items',
      'dumpster accepted items',
      'dumpster restricted items',
      'hazardous waste disposal',
      'dumpster rules',
    ],
  },
  '/dumpster-rental-guide': {
    title: 'Complete Dumpster Rental Guide | RED-E-BINS Minnesota',
    description:
      'New to renting a dumpster? Our dumpster rental guide gives you all the info you need, from tips and tricks and answers to common dumpster rental questions.',
    keywords: [
      'dumpster rental guide',
      'how to rent a dumpster',
      'dumpster rental tips',
      'first time dumpster rental',
      'dumpster rental FAQ',
    ],
  },
  '/about-us': {
    title:
      "Need Assistance? We're All About Making It Easy To Get Your Project Done.",
    description:
      "Nice to meet you. At RED-E-BINS, we're all about making it easy to get your project done. Quick online scheduling. Need assistance?",
    keywords: [
      'about RED-E-BINS',
      'RED-E-BINS Minnesota',
      'dumpster rental company',
      'Twin Cities dumpster service',
      'local dumpster rental',
    ],
  },
  '/debris-calculator': {
    title:
      'Dumpster Size Debris Calculator | Estimate Your Debris with RED-E-BINS',
    description:
      "Not sure how much space you need? Our debris calculator helps you choose the right dumpster size for your project—no guesswork, just simple solutions.",
    keywords: [
      'debris calculator',
      'dumpster size calculator',
      'how much debris',
      'dumpster size estimator',
      'waste volume calculator',
    ],
  },
  '/service-areas': {
    title: 'Dumpster Rental "Near Me" | Twin Cities Metro Area | RED-E-BINS',
    description:
      'Searching for a dumpster rental "near me"? RED-E-BINS offers affordable dumpsters in Minneapolis, Saint Paul, and the Twin Cities metro area.',
    keywords: [
      'dumpster rental near me',
      'Twin Cities dumpster rental',
      'Minneapolis dumpster rental',
      'St. Paul dumpster rental',
      'metro area dumpster service',
    ],
  },
  '/dumpster-sizes': {
    title:
      'Dumpster Sizes | 5-Yard & 10-Yard Rentals | RED-E-BINS Minnesota',
    description:
      'Compare RED-E-BINS dumpster sizes. The 5YD (8×4×4) holds about 2 pickup truck loads starting at $329. The 10YD (10×5×5) holds about 4 starting at $379. Both fit in a standard driveway.',
    keywords: [
      'dumpster sizes Minneapolis MN',
      '5 yard dumpster rental MN',
      '10 yard dumpster rental MN',
      'small dumpster rental Minneapolis',
      'which dumpster size do I need',
    ],
  },
  '/faq': {
    title: 'FAQ | Common Dumpster Rental & Junk Removal Questions | RED-E-BINS',
    description:
      "Got questions? We've got answers. Check out our FAQ for everything you need to know about renting a dumpster or booking junk removal. We make it easy!",
    keywords: [
      'dumpster rental FAQ',
      'junk removal questions',
      'dumpster rental answers',
      'common dumpster questions',
      'RED-E-BINS FAQ',
    ],
  },
  '/how-we-measure-up': {
    title: 'Compare Us to Other Service | How We Measure Up | RED-E-BINS',
    description:
      'Compare RED-E-BINS to traditional dumpsters, brokers, green bags, and junk removal services. See how we measure up to the competition.',
    keywords: [
      'dumpster comparison',
      'RED-E-BINS vs competition',
      'dumpster rental comparison',
      'junk removal comparison',
      'best dumpster service',
    ],
  },
};

/**
 * Get page-specific SEO data for a given pathname
 * Returns null if no page-specific SEO is configured (will use template defaults)
 */
export function getPageSEO(pathname: string): PageSEO | null {
  // Normalize pathname by removing trailing slash (except for root)
  const normalizedPath =
    pathname === '/' ? pathname : pathname.replace(/\/$/, '');
  return REDEBINS_SEO_CONFIG[normalizedPath] || null;
}
